import React from 'react';
import { Button } from '@/components/ui/button';

interface Props { 
  children: React.ReactNode 
}

interface State { 
  hasError: boolean; 
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

const isDev = import.meta.env.DEV;

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Only log in development to avoid exposing internals
    if (isDev) {
      console.error('❌ ErrorBoundary caught:', error);
      console.error('Component stack:', errorInfo.componentStack);
    }
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-background">
          <div className="max-w-md w-full space-y-6 text-center">
            <div className="space-y-3">
              <div className="text-6xl">🛠️</div>
              <h1 className="text-2xl font-bold text-foreground">
                We're having a moment
              </h1>
              <p className="text-muted-foreground">
                Something unexpected happened. Please try refreshing the page or come back in a few minutes.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => window.location.reload()} size="lg">
                Refresh Page
              </Button>
              <Button onClick={this.handleReset} variant="outline" size="lg">
                Go to Home
              </Button>
            </div>
            
            {/* Only show technical details in development */}
            {isDev && this.state.error && (
              <details className="text-xs bg-muted p-4 rounded-lg text-left mt-6">
                <summary className="cursor-pointer font-medium mb-2">
                  Developer info (dev only)
                </summary>
                <pre className="overflow-auto whitespace-pre-wrap break-words text-destructive">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
